<div class="whatsapp-conversation d-flex flex-column" data-testid="conversation-panel">
    <div class="conversation-header px-3 py-2 border-bottom">
        <span class="fw-semibold"><i class="bi bi-whatsapp text-success me-2"></i>Conversation</span>
    </div>

    <div #messagesContainer class="conversation-messages flex-grow-1 px-3 py-3" data-testid="conversation-messages">
        <div *ngIf="!loading && items.length === 0" class="text-center text-muted py-4" data-testid="conversation-empty-state">
            No conversation yet.
        </div>

        <div *ngIf="hasMore" class="text-center mb-2">
            <button type="button"
                    class="btn btn-sm btn-outline-secondary"
                    [disabled]="loadingOlder"
                    (click)="loadOlder()"
                    data-testid="conversation-load-older-btn">
                <span *ngIf="loadingOlder" class="spinner-border spinner-border-sm me-1" role="status"></span>
                Load earlier messages
            </button>
        </div>

        <!-- trackBy is load-bearing: the poll replaces `items` wholesale every 10s, so without
             it Angular destroys and recreates every bubble, which stops any playing audio/video. -->
        <div *ngFor="let item of items; trackBy: trackByItemId" data-conversation-item
             class="d-flex mb-2"
             [class.justify-content-end]="item.direction === 'outbound'"
             [class.justify-content-start]="item.direction === 'inbound'">

            <ng-container *ngIf="item.direction === 'outbound'">
                <div class="chat-bubble chat-bubble-sent" data-testid="conversation-bubble-sent">
                    <div class="chat-bubble-text" data-testid="conversation-bubble-text">{{ outboundLabel(item) }}</div>
                    <div *ngIf="item.media as media" class="chat-bubble-media">
                        <ng-container *ngFor="let m of media; trackBy: trackByMediaUrl">
                            <ng-container *ngTemplateOutlet="mediaAttachment; context: {$implicit: m}"></ng-container>
                        </ng-container>
                    </div>
                    <div class="chat-bubble-timestamp" data-testid="conversation-bubble-timestamp">
                        {{ item.timestamp | date:"dd/MM/yyyy 'at' HH:mm" }}
                    </div>
                    <div class="chat-bubble-status" data-testid="conversation-bubble-status">{{ item.status }}</div>
                </div>
            </ng-container>

            <ng-container *ngIf="item.direction === 'inbound'">
                <div class="chat-bubble chat-bubble-received" data-testid="conversation-bubble-received">
                    <div class="chat-bubble-text" data-testid="conversation-bubble-text">{{ item.text }}</div>
                    <div *ngIf="item.media as media" class="chat-bubble-media">
                        <ng-container *ngFor="let m of media; trackBy: trackByMediaUrl">
                            <ng-container *ngTemplateOutlet="mediaAttachment; context: {$implicit: m}"></ng-container>
                        </ng-container>
                    </div>
                    <div class="chat-bubble-timestamp" data-testid="conversation-bubble-timestamp">
                        {{ item.timestamp | date:"dd/MM/yyyy 'at' HH:mm" }}
                    </div>
                </div>
            </ng-container>
        </div>

        <div #scrollAnchor></div>
    </div>

    <!-- Window-closed notice: outside the 24h Twilio session window -->
    <div *ngIf="windowClosed"
         class="alert alert-warning d-flex align-items-center justify-content-between mx-3 mb-2 py-2"
         data-testid="conversation-window-closed-notice">
        <span>This customer hasn't messaged in the last 24 hours — send a template message instead.</span>
        <button type="button" class="btn-close" aria-label="Dismiss" (click)="dismissWindowClosedNotice()"></button>
    </div>

    <!-- Generic error notice: initial load / load-older / send failures only (polls are silent) -->
    <div *ngIf="errorMessage"
         class="alert alert-danger d-flex align-items-center justify-content-between mx-3 mb-2 py-2"
         data-testid="conversation-error-notice">
        <span>{{ errorMessage }}</span>
        <button type="button" class="btn btn-sm btn-outline-danger ms-2" (click)="loadInitial()" data-testid="conversation-retry-btn">Retry</button>
        <button type="button" class="btn-close" aria-label="Dismiss" (click)="dismissError()"></button>
    </div>

    <div class="conversation-input d-flex align-items-center gap-2 px-3 py-3 border-top">
        <textarea
            class="form-control"
            rows="1"
            placeholder="Type a message…"
            [formControl]="messageControl"
            [maxlength]="maxLength"
            (keydown.enter)="$event.preventDefault(); send()"
            data-testid="conversation-input">
        </textarea>
        <button
            type="button"
            class="btn btn-success rounded-pill"
            [disabled]="isSendDisabled"
            (click)="send()"
            data-testid="conversation-send-btn">
            <span *ngIf="sending" class="spinner-border spinner-border-sm" role="status"></span>
            <!-- bi-send doesn't exist in the pinned bootstrap-icons@1.2.2 (renders blank) -->
            <i *ngIf="!sending" class="bi bi-arrow-right"></i>
        </button>
    </div>
</div>

<!-- WhatsApp media display (design 2026-07-30 §6): one attachment.
     Blob fetched with bearer auth and cached as an object URL; render switches on the
     contentType stored at ingest. While the blob is in flight — or after a failed fetch
     (unavailable state) — the chip stays. -->
<ng-template #mediaAttachment let-m>
    <ng-container *ngIf="mediaSafeUrl(m) as safeUrl; else mediaChip">
        <!-- image/*: inline thumbnail, click opens the full-screen overlay below -->
        <button *ngIf="isMediaImage(m)" type="button" class="chat-bubble-media-image-btn"
                (click)="openLightbox(m)" [attr.aria-label]="'Preview ' + m.contentType"
                data-testid="conversation-media-image-link">
            <img [src]="safeUrl" [alt]="m.contentType" class="chat-bubble-media-image"
                 data-testid="conversation-media-image">
        </button>
        <!-- audio/* and video/*: fully buffered blob URL, so seeking works without server Range
             support. preload="auto" because the bytes are already in memory: the default
             (metadata) defers demux/decode until the play click, which adds a visible delay
             before the first frame. Buffering up front costs no extra network here. -->
        <audio *ngIf="isMediaAudio(m)" controls preload="auto" [src]="safeUrl" class="chat-bubble-media-audio"
               data-testid="conversation-media-audio"></audio>
        <!-- Video keeps native click-to-play; expanding is a separate control. -->
        <span *ngIf="isMediaVideo(m)" class="chat-bubble-media-video-wrap">
            <video controls preload="auto" [src]="safeUrl" class="chat-bubble-media-video"
                   data-testid="conversation-media-video"></video>
            <button type="button" class="chat-bubble-media-expand" (click)="openLightbox(m)"
                    aria-label="Expand video to full screen"
                    data-testid="conversation-media-video-expand">
                <i class="bi bi-arrows-fullscreen"></i>
            </button>
        </span>
        <!-- everything else: a link labelled with the type -->
        <a *ngIf="!isMediaImage(m) && !isMediaAudio(m) && !isMediaVideo(m)"
           [href]="safeUrl" target="_blank" rel="noopener"
           class="chat-bubble-media-link" data-testid="conversation-media-link">{{ m.contentType }}</a>
    </ng-container>
    <ng-template #mediaChip>
        <span class="chat-bubble-media-chip"
              [class.chat-bubble-media-chip-unavailable]="isMediaUnavailable(m)"
              data-testid="conversation-media-chip">{{ mediaChipLabel(m) }}</span>
    </ng-template>
</ng-template>

<!-- Full-screen preview for images and video. Click the backdrop or press Escape to close. -->
<div *ngIf="lightboxUrl as lightbox" class="media-lightbox" (click)="closeLightbox()"
     role="dialog" aria-modal="true" aria-label="Attachment preview"
     data-testid="conversation-media-lightbox">
    <button type="button" class="media-lightbox-close" aria-label="Close preview"
            (click)="closeLightbox()" data-testid="conversation-media-lightbox-close">
        <i class="bi bi-x-lg"></i>
    </button>
    <!-- stopPropagation so interacting with the media doesn't dismiss the overlay -->
    <video *ngIf="lightboxIsVideo; else lightboxImage" controls autoplay [src]="lightbox"
           class="media-lightbox-media" (click)="$event.stopPropagation()"
           data-testid="conversation-media-lightbox-video"></video>
    <ng-template #lightboxImage>
        <img [src]="lightbox" alt="Attachment preview" class="media-lightbox-media"
             (click)="$event.stopPropagation()" data-testid="conversation-media-lightbox-image">
    </ng-template>
</div>
